import {
    useCallback,
    useMemo,
} from 'react'
import {
    Navigate,
    Outlet,
    useNavigation,
    useOutletContext,
    useResolvedPath,
} from 'react-router-dom'
import Box from '@mui/material/Box'

import {
    useUserRoles,
} from 'app/Auth'
import hasPermission from 'app/utils/hasPermission'
import {
    SidebarTab,
} from 'app/shared-components/SkyNetDomain'
import SkyNetTabs from 'app/shared-components/SkyNetTabs'
import useDetailedViewOutlet
    from 'app/shared-components/SkyNetObjectProfile/hooks/useDetailedViewOutlet'
import Loading, {
    useDelayedLoading,
} from 'app/shared-components/Loading'

import {
    SkyNetObjectFormOutletContext,
    SkyNetObjectTabsOutletContext,
} from '../../SkyNetObjectRoutes.types'
import CloseButton from '../CloseButton'
import useStyles from './SkyNetObjectProfileTabs.styles'

/* Component for Tabs for Detailed View Forms  */
function SkyNetObjectProfileTabs({
    forms,
}: Readonly<{
    forms: SidebarTab[],
}>) {
    const roles = useUserRoles()
    const {
        activeTabName: tableTabName,
        locationPathname,
        tablePath,
        id,
        navigate,
    } = useDetailedViewOutlet()
    const {
        classes,
    } = useStyles()
    const {
        pathname: backPath,
    } = useResolvedPath('..')
    const {
        refreshTable,
    } = useOutletContext<SkyNetObjectTabsOutletContext>()
    const navigation = useNavigation()

    const isLoading = useMemo(() => {
        return navigation.state === 'loading'
    }, [navigation.state])

    const showLoading = useDelayedLoading({
        loadingTriggered: isLoading,
    })

    const tabs = useMemo(() => {
        return forms.filter(({
            accessOnly,
        }) => {
            return !accessOnly?.length || hasPermission(accessOnly, roles)
        }).map(({
            label, url,
        }) => {
            return {
                name: label,
                url,
            }
        })
    }, [
        forms,
        roles,
    ])

    const findTabIndex = useCallback((tabName) => {
        return tabs.findIndex((tab) => {
            return tab.url === tabName
        })
    }, [tabs])

    const activeTabName = useMemo(() => {
        const regex = new RegExp(`${tablePath}/${tableTabName}/${id}/([^/]+)`)
        const match = locationPathname.match(regex)
        const tabName = match ? tabs[findTabIndex(match[1])]?.url : null

        return tabName
    }, [
        id,
        locationPathname,
        tablePath,
        tableTabName,
        tabs,
        findTabIndex,
    ])

    const getNewTabUrl = useCallback((tabIndex: number) => {
        return `${tablePath}/${tableTabName}/${id}/${tabs[tabIndex].url}`
    }, [
        id,
        tablePath,
        tableTabName,
        tabs,
    ])

    const onActiveTabChange = (newValue: number) => {
        navigate(getNewTabUrl(newValue))
    }

    const activeTab = useMemo(() => {
        const tabIndex = findTabIndex(activeTabName)

        return tabIndex > -1 ? tabIndex : 0
    }, [
        findTabIndex,
        activeTabName,
    ])

    const RedirectElement = useMemo(() => {
        if (activeTabName) return null
        return (
            <Navigate
                to={getNewTabUrl(0)}
                replace
            />
        )
    }, [
        activeTabName,
        getNewTabUrl,
    ])

    const onClose = () => {
        navigate(backPath)
    }

    /* passing custom props to components rendered in the Outlet */
    const outletContext: SkyNetObjectFormOutletContext = useMemo(() => {
        return {
            backPath,
            refreshTable,
        }
    }, [
        backPath,
        refreshTable,
    ])

    return (
        <Box sx={{
            flex: 1,
            minWidth: 0,
        }}
        >
            <div className={classes.root}>
                <SkyNetTabs
                    value={activeTab}
                    tabs={tabs}
                    onChange={onActiveTabChange}
                />
                <CloseButton onClose={onClose} />
            </div>
            <div className={classes.contentWrapper}>
                <div className={classes.content}>
                    {showLoading ? (
                        <div className={classes.loadingWrapper}>
                            <Loading />
                        </div>
                    ) : <Outlet context={outletContext} />}
                </div>
            </div>
            {RedirectElement}
        </Box>
    )
}

export default SkyNetObjectProfileTabs
